// client/src/components/services_tester/ServicesTesterActions.jsx
import React from 'react';
import {
  Button,
  Stack,
  Box,
  Typography,
  Chip,
  Paper,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Refresh,
  Dns,
  Hub,
  Route,
  PlayArrow,
  Stop,
} from '@mui/icons-material';

import PagePanel from '../common/PagePanel';

export default function ServicesTesterActions({
  healthCheckLoading,
  authLoading,
  isAuthenticated,
  handleQuickHealthCheck,
  handleCompleteHealthCheck,
  handleInfrastructureCheck,
  handleLogin,
  handleCheckAuth,
  handleLogout,
  setRoutesModal,
  autoRefresh,
  setAutoRefresh,
  healthyServicesCount,
  accessibleServices,
  totalServicesCount,
  authRequiredServices,
  rabbitmqStatus,
  rabbitmqQueuesCount,
  rabbitmqConnectionsCount,
  rabbitmqMessagesCount,
  kratosStatus,
  kratosUsersCount,
  kratosActiveUsers,
  featureTreeStatus,
  featureTreeNodesCount,
  featureTreeVersion,
}) {
  return (
    <PagePanel sx={{ flexShrink: 0 }}>
      <Box sx={{ py: 1, px: 1.5 }}>
        {/* Кнопки */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ rowGap: 0.75 }}
        >
          <Button
            variant="contained"
            size="small"
            startIcon={
              healthCheckLoading ? <CircularProgress size={14} color="inherit" /> : <Refresh fontSize="small" />
            }
            onClick={handleQuickHealthCheck}
            disabled={healthCheckLoading}
            sx={{ fontSize: '0.7rem', py: 0.5 }}
          >
            {healthCheckLoading ? 'Проверка…' : 'Быстрая проверка'}
          </Button>

          <Button
            variant="outlined"
            size="small"
            startIcon={<Dns fontSize="small" />}
            onClick={handleCompleteHealthCheck}
            disabled={healthCheckLoading}
            sx={{ fontSize: '0.7rem', py: 0.5 }}
          >
            Полная проверка
          </Button>

          <Button
            variant="outlined"
            size="small"
            startIcon={<Hub fontSize="small" />}
            onClick={handleInfrastructureCheck}
            disabled={healthCheckLoading}
            sx={{ fontSize: '0.7rem', py: 0.5 }}
          >
            Инфраструктура
          </Button>

          <Box sx={{ flex: 1 }} />

          <Tooltip title="Просмотр API routes всех сервисов" arrow>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Route fontSize="small" />}
              onClick={() => setRoutesModal(true)}
              sx={{ fontSize: '0.7rem', py: 0.5 }}
            >
              API Routes
            </Button>
          </Tooltip>

          <Tooltip
            title={
              autoRefresh
                ? 'Остановить автообновление (30 сек)'
                : 'Автообновление каждые 30 секунд'
            }
            arrow
          >
            <Chip
              icon={autoRefresh ? <Stop fontSize="small" /> : <PlayArrow fontSize="small" />}
              label="Авто"
              color={autoRefresh ? 'primary' : 'default'}
              variant={autoRefresh ? 'filled' : 'outlined'}
              onClick={() => setAutoRefresh(!autoRefresh)}
              clickable
              size="small"
              sx={{
                height: 22,
                fontSize: '0.65rem',
                '& .MuiChip-label': { px: 0.75 },
                '& .MuiChip-icon': { ml: 0.5, mr: -0.25, fontSize: 12 },
              }}
            />
          </Tooltip>
        </Stack>

        {/* Метрики — компактно */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 1, rowGap: 0.5 }}
        >
          {/* Auth */}
          <Paper
            elevation={0}
            sx={{
              py: 0.25,
              px: 0.75,
              bgcolor: isAuthenticated ? 'success.light' : 'warning.light',
              borderRadius: 1,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Typography
              variant="caption"
              fontWeight="bold"
              sx={{ fontSize: '0.6rem', color: 'white', lineHeight: 1 }}
            >
              Auth:
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontSize: '0.6rem', color: 'white', lineHeight: 1 }}
            >
              {isAuthenticated ? 'OK' : 'OFF'}
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontSize: '0.6rem', color: 'white', lineHeight: 1, opacity: 0.85 }}
            >
              ({accessibleServices}/{totalServicesCount})
            </Typography>
          </Paper>

          {/* RabbitMQ */}
          {rabbitmqStatus?.status === 'connected' && (
            <Paper
              elevation={0}
              sx={{
                py: 0.25,
                px: 0.75,
                bgcolor: 'success.light',
                borderRadius: 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Typography
                variant="caption"
                fontWeight="bold"
                sx={{ fontSize: '0.6rem', color: 'white', lineHeight: 1 }}
              >
                RabbitMQ:
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'white', lineHeight: 1 }}>
                {rabbitmqQueuesCount}Q / {rabbitmqConnectionsCount}C / {rabbitmqMessagesCount}M
              </Typography>
            </Paper>
          )}

          {/* Kratos users */}
          {kratosStatus?.users?.status === 'success' && (
            <Paper
              elevation={0}
              sx={{
                py: 0.25,
                px: 0.75,
                bgcolor: 'info.light',
                borderRadius: 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Typography
                variant="caption"
                fontWeight="bold"
                sx={{ fontSize: '0.6rem', color: 'white', lineHeight: 1 }}
              >
                Kratos:
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'white', lineHeight: 1 }}>
                {kratosUsersCount} users ({kratosActiveUsers} active)
              </Typography>
            </Paper>
          )}

          {/* FeatureTree (deprecated) */}
          {featureTreeStatus?.status === 'success' && (
            <Paper
              elevation={0}
              sx={{
                py: 0.25,
                px: 0.75,
                bgcolor: 'success.main',
                borderRadius: 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Typography
                variant="caption"
                fontWeight="bold"
                sx={{ fontSize: '0.6rem', color: 'white', lineHeight: 1 }}
              >
                FeatureTree:
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'white', lineHeight: 1 }}>
                {featureTreeNodesCount} nodes (v{featureTreeVersion})
              </Typography>
            </Paper>
          )}
        </Stack>
      </Box>
    </PagePanel>
  );
}
